// Client-side counterpart of ApplicationController#accept_language_locale:
// which supported UI language the browser prefers, and whether a language
// suggestion (the marketing pages' "this page is also available in …"
// banner) should be offered on top of the page's own language.
//
// Detection runs in the browser on purpose — the marketing HTML is
// edge-cached and visitor-neutral (see GeoController), so per-visitor
// signals must never decide what the server renders. The signed :locale
// cookie is httponly and invisible here; explicit choices are remembered
// in localStorage instead (recorded by setLocale, so using any language
// switcher also silences the banner).
import { LANGUAGES, baseLanguage } from "@/lib/locale-routes"

// Mirrors ApplicationController::ACCEPT_LANGUAGE_ALIASES.
const ALIASES: Record<string, string> = {}

const KEY = "toby:language-choice"

/** The language the banner should offer: the first supported base language
 *  in the browser's preference list (navigator.languages), or null when
 *  that preference is already the page's base language — a lower-ranked
 *  match must not nag someone whose first choice is on screen. */
export function suggestedLanguage(
  preferred: readonly string[],
  currentCode: string,
): string | null {
  const current = baseLanguage(currentCode)
  for (const entry of preferred) {
    const base = baseLanguage(entry.toLowerCase())
    const lang = ALIASES[base] ?? base
    if (!LANGUAGES.includes(lang)) continue
    return lang === current ? null : lang
  }
  return null
}

/** The language the visitor explicitly picked or dismissed the banner in
 *  favor of, if any — any recorded choice suppresses future suggestions. */
export function storedLanguageChoice(): string | null {
  try {
    const value = localStorage.getItem(KEY)
    return value && LANGUAGES.includes(value) ? value : null
  } catch {
    return null
  }
}

export function rememberLanguageChoice(lang: string): void {
  try {
    localStorage.setItem(KEY, baseLanguage(lang))
  } catch {
    // Private mode / SSR — the visitor may see the suggestion again.
  }
}
